/** A tiny history router: the app has only a handful of routes (implementation plan §4.4). */
import { useSyncExternalStore } from "react";

export type Route =
  | { readonly name: "landing"; readonly code: string | null }
  | { readonly name: "play"; readonly code: string }
  | { readonly name: "screen" }
  /** A big screen's pairing QR, scanned with the phone's own camera app (§15.8). */
  | { readonly name: "pair"; readonly code: string }
  | { readonly name: "devTable" }
  /** The printable rules handout (basics, roles and cards). */
  | { readonly name: "rules" }
  /** The intro video (§17.4). */
  | { readonly name: "intro" }
  | { readonly name: "notFound" };

export function parseRoute(pathname: string): Route {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length === 0) return { name: "landing", code: null };
  const [head, arg] = parts;
  if (head === "join" && arg && parts.length === 2)
    return { name: "landing", code: arg.toUpperCase() };
  if (head === "play" && arg && parts.length === 2)
    return { name: "play", code: arg.toUpperCase() };
  if (head === "screen" && parts.length === 1) return { name: "screen" };
  if (head === "pair" && arg && parts.length === 2)
    return { name: "pair", code: arg.toUpperCase() };
  if (head === "rules" && parts.length === 1) return { name: "rules" };
  if (head === "intro" && parts.length === 1) return { name: "intro" };
  if (head === "dev" && arg === "table" && parts.length === 2) return { name: "devTable" };
  return { name: "notFound" };
}

const listeners = new Set<() => void>();

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  window.addEventListener("popstate", cb);
  return () => {
    listeners.delete(cb);
    window.removeEventListener("popstate", cb);
  };
}

export function navigate(path: string, replace = false): void {
  if (replace) window.history.replaceState(null, "", path);
  else window.history.pushState(null, "", path);
  for (const l of listeners) l();
}

export function usePathname(): string {
  return useSyncExternalStore(
    subscribe,
    () => window.location.pathname,
    () => "/",
  );
}
